import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHighYieldUnits } from '../../../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../../../stores/pyqAnalyticsStore'
import LoadingSection from '../shared/LoadingSection'
import ErrorSection from '../shared/ErrorSection'
import SubjectTabs from '../shared/SubjectTabs'
import { HighYieldBarChart } from '../charts'
import { SUBJECT_COLORS } from '../../../constants/colors'
import type { SubjectCode } from '../../../types'
import { AnalyticsLockedOverlay } from '@/features/payments'

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string, subjectName?: string): string {
  const code = (subjectCode || subjectName || '').toUpperCase()

  if (code.includes('PHY')) {
    return SUBJECT_COLORS.NEET_UG_PHY
  }
  if (code.includes('CHEM')) {
    return SUBJECT_COLORS.NEET_UG_CHEM
  }
  if (code.includes('BIO') || code.includes('BOTANY') || code.includes('ZOOLOGY')) {
    return SUBJECT_COLORS.NEET_UG_BIO
  }

  return '#6B7280' // Default gray
}

interface HighYieldUnitsSectionProps {
  isLocked?: boolean
}

export default function HighYieldUnitsSection({ isLocked = false }: HighYieldUnitsSectionProps) {
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()
  const [selectedSubject, setSelectedSubject] = useState<SubjectCode>('NEET_UG_PHY')

  const handleUnitClick = (unitCode: string) => {
    navigate(`/analytics/past-exams/unit/${unitCode}`)
  }

  const { data, isLoading, isError, refetch } = useHighYieldUnits({
    yearRangeStart,
    yearRangeEnd,
    subjectCode: selectedSubject,
    limit: 10,
  })

  if (isLoading) {
    return <LoadingSection title="High-Yield Units" height="h-[500px]" />
  }

  if (isError) {
    return (
      <ErrorSection
        title="Failed to load high-yield units"
        onRetry={() => refetch()}
        height="h-[500px]"
      />
    )
  }

  if (!data) return null

  const units = data.units || []

  const contentGrid = (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* Bar Chart */}
      <div>
        <HighYieldBarChart
          data={units.slice(0, 10).map((u) => ({
            unitCode: u.unitCode,
            unitName: u.unitName,
            subjectCode: u.subjectCode,
            subjectName: u.subjectName,
            totalQuestions: u.totalQuestions,
            avgQuestionsPerExam: u.avgQuestionsPerExam,
            importanceScore: u.importanceScore,
          }))}
          onUnitClick={handleUnitClick}
        />
      </div>

      {/* Mobile: Cards */}
      <div className="md:hidden space-y-3">
        {units.slice(0, 10).map((unit, index) => (
          <div
            key={unit.unitCode}
            onClick={() => handleUnitClick(unit.unitCode)}
            className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <span
                className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white rounded-full"
                style={{ backgroundColor: getSubjectColor(unit.subjectCode, unit.subjectName) }}
              >
                {index + 1}
              </span>
            </div>
            <h4 className="font-medium text-gray-900 text-sm mb-3">{unit.unitName}</h4>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-500">Questions</p>
                <p className="font-semibold text-gray-900">{unit.totalQuestions}</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-500">Avg/Exam</p>
                <p className="font-semibold text-gray-900">{(unit.avgQuestionsPerExam ?? 0).toFixed(1)}</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-gray-500">Importance</p>
                <p className="font-semibold text-gray-900">{(unit.importanceScore ?? 0).toFixed(1)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 font-medium text-gray-600">#</th>
              <th className="text-left py-2 px-3 font-medium text-gray-600">Unit</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600">Questions</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600">Avg/Exam</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600">Importance</th>
            </tr>
          </thead>
          <tbody>
            {units.slice(0, 10).map((unit, index) => (
              <tr
                key={unit.unitCode}
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleUnitClick(unit.unitCode)}
              >
                <td className="py-2.5 px-3 text-gray-500">{index + 1}</td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-1 h-8 rounded-full"
                      style={{ backgroundColor: getSubjectColor(unit.subjectCode, unit.subjectName) }}
                    />
                    <span className="font-medium text-gray-900">{unit.unitName}</span>
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right font-medium text-gray-900">
                  {unit.totalQuestions}
                </td>
                <td className="py-2.5 px-3 text-right text-gray-600">
                  {(unit.avgQuestionsPerExam ?? 0).toFixed(1)}
                </td>
                <td className="py-2.5 px-3 text-right text-gray-600">
                  {(unit.importanceScore ?? 0).toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header with Subject Tabs - always visible */}
      <div className="flex flex-col gap-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">High-Yield Units</h3>
        <SubjectTabs
          selectedSubject={selectedSubject}
          onSubjectChange={setSelectedSubject}
        />
      </div>

      {/* Content - locked for free users */}
      {isLocked ? (
        <AnalyticsLockedOverlay>
          {contentGrid}
        </AnalyticsLockedOverlay>
      ) : (
        contentGrid
      )}
    </div>
  )
}
